
import {Component} from "vue-property-decorator";
import FileInput from "@/components/common/FileInput.vue";
import BaseDynamicComponent from "@/app/dynamic-components/forms/form-components/form-component.base";
import {documentServiceV2} from "@/app/services/document.service";
import {DocumentFile} from "@/app/models/document/document.model";
import Loader from "@/components/common/Loader.vue";
import AutocompleteWithErrors from "@/app/components/AutocompleteWithErrors.vue";
import DragAndDropFileInput from "@/app/components/DragAndDropFileInput.vue";
import store from "@/store";
import DynamicComponentConfigManager from "@/app/components/configmanager/dynamic-component-config-manager.vue";
import {partyService} from "@/app/services/party.service";
import {resolveEngine} from "@/app/helpers/resolve.engine";
import {ContextAware} from "@/app/contexts/detail.context";
import {ruleEngine} from "@/app/services/rule.engine";

export type FileInputMetaData = {
  accept?: string;
  iconOnRight?: boolean;

  mode?: string;

  multiple?: boolean;

  categoryId: string;
  categoryIdTemplate: string;

  typeIds: string[];
  typeIdsTemplate: string;

  attachDynamicProperties: boolean;
  additionalDynamicProperties: {[key: string]: any};
  additionalDynamicPropertiesIndexers: {[key: string]: string};

  description: string;
  descriptionTemplate: string;

  alternateReferenceId: string;
  alternateReferenceType: string;

  keepDocumentListAlive: boolean;

  isPublic: string;

  involvedParties: {
    portalId : string //reference to identify the reason the user was added as party
    roleId? : string //role of user/org to grant to
    contactId? : string //id of user/org to grant to,
    partyRoleId?: string, //roleid of the user/org it represents
    contactType : ("USER_IN_CURRENT_ORGANISATION" |    "USER" |    "ORGANISATION" |    "ALL_USERS" |    "BCCA" |    "USERS_WITH_ROLE" |    "ORGANISATIONS_WITH_ROLE")
  }[]

  concurrency: number;
};

@Component({
  components: {
    DynamicComponentConfigManager,
    DragAndDropFileInput,
    AutocompleteWithErrors,
    Loader,
    FileInput,
  },
})
export default class FileInputBase extends BaseDynamicComponent<FileInputMetaData> {
  documents: DocumentFile[] = [];
  internalValue: File[] = [];
  isProcessing = false;
  isUploading = false;
  amountOfUploadedDocuments = 0;
  totalAmountOfDocuments = 0;
  currentUploadingDocument: string[] = [];

  async pressedFile(index: number) {
    if (this.documents && this.documents.length > index && this.documents[index] && this.value && Array.isArray(this.value) && this.value.length > index && this.value[index]) {
      const doc: DocumentFile = this.documents[index];
      const docId = this.value[index];
      await documentServiceV2.downloadDocumentById(docId);
    }
  }

  async postSetValueView(value: any) {
    // new value being set
    //empty is a reset
    if (!value) {
      this.documents = [];
    }

    if (!Array.isArray(value)) {
      value = [value];
    }
    const currentDocumentIds = this.documents.map(
        (value1) => value1.documentId
    );

    if (JSON.stringify(value) === JSON.stringify(currentDocumentIds)) {
      return;
    }

    for (let documentId of value) {
      if (documentId && !currentDocumentIds.includes(documentId)) {
        const doc = new DocumentFile();
        doc.setUploadedFile(documentId);
        this.documents.push(doc);
      }
    }
    await this.resolveDocuments(false);
  }

  onFileInputContentChanged(newValue: File[]) {
    const currentDocumentNames = this.documents.map((value1) => value1.name);

    if(!Array.isArray(newValue) && newValue !== null && newValue !== undefined && typeof newValue === 'object'){
      newValue = [newValue];
    }

    //add files
    for (let file of newValue) {
      const alreadyExisist = currentDocumentNames.includes(file.name);
      if (!alreadyExisist) {
        const doc = new DocumentFile();
        doc.setNewFile(file, file.name);
        this.documents.push(doc);
      }
    }

    //remove files
    const newFileNames = newValue.map((value) => value.name);
    this.documents = this.documents.filter((document) => {
      return document.name && newFileNames.includes(document.name);
    });

    this.resolveDocuments(false);
  }

  onFileInputValidityChanged(isValid: boolean) {
    return;
  }

  async beforeSave(): Promise<boolean> {
    let hasError = false;

    if (this.entry.value && Array.isArray(this.entry.value)) {
      this.isUploading = true;
      this.totalAmountOfDocuments = this.documents.length;
      // create and upload all docs
      const toUploadDocs = this.documents.filter((doc) => !doc.uploaded);
      let CONCURRENCY = this.metadata?.concurrency || 4;
      if(CONCURRENCY <= 0) CONCURRENCY = 1;


      const pool = new Set<Promise<boolean>>();
      for (const doc of toUploadDocs) {
        const p = this.uploadDocument(doc).then((ok) => {
          if (!ok) hasError = true;
          return ok;
        });
        pool.add(p);
        p.finally(() => pool.delete(p));
        if (pool.size >= CONCURRENCY) {
          await Promise.race(pool);
        }
      }
      await Promise.all(pool);
      this.currentUploadingDocument = [];

      if (hasError) {
        return false;
      }

      if (!this.entry.metadata.keepDocumentListAlive) {
        this.isUploading = false;
        this.amountOfUploadedDocuments = 0;
        this.totalAmountOfDocuments = 0;
      }

      await this.resolveDocuments(true);
      return true;
    }
    return true;
  }

  async afterSave(sumbitData: any): Promise<boolean> {
    if (this.entry.value && Array.isArray(this.entry.value) && this.documents) {
      await this.adoptDocuments(sumbitData);
      
      // Handle isPublic parameter to automatically add all users as party
      let partiesToAdd = this.metadata?.involvedParties || [];

      if(partiesToAdd && Array.isArray(partiesToAdd)){
        for (let partiesToAddElement of partiesToAdd) {
          if(partiesToAddElement && partiesToAddElement.contactId && partiesToAddElement.contactId.startsWith("data.")){
            partiesToAddElement.contactId = resolveEngine.resolveFieldValue(partiesToAddElement.contactId, this.getContext() as ContextAware);
          }
        }
      }

      if (this.metadata?.isPublic && this.getContext() != undefined) {
          const isPublic = await ruleEngine.resolveRuleAsync(this.getContext() as ContextAware, this.metadata.isPublic, this);
          if(isPublic){
            // Add ALL_USERS party if not already present
            const hasAllUsersParty = partiesToAdd.some(party => party.contactType === "ALL_USERS");

            if (!hasAllUsersParty) {
              partiesToAdd.push({
                portalId: "public-document",
                contactType: "ALL_USERS"
              });
            }
          }
      }

      console.warn('partiesToAdd', partiesToAdd)
      
      await this.addParties(partiesToAdd, this.documents);
      return true;
    }
    return true;
  }

  public downloadFile(doc: DocumentFile) {
    if (doc.fetched && doc.uploaded) {
      documentServiceV2
          .downloadDocument(doc)
          .then((value) => {
            return;
          })
          .catch((reason) => {
            this.$toast.error("Something went wrong grabbing your file.");
          });
    }
  }

  private async resolveDocuments(setDataImmediate: boolean) {
    this.isProcessing = true;
    for (let doc of this.documents) {
      if (doc.uploaded && !doc.fetched && doc.documentId) {
        var documentMetadata = await documentServiceV2.getDocumentMetadata(
            doc.documentId
        );
        doc.setFetchedMetadata(documentMetadata);
      } else if (!doc.uploaded && !doc.fetched) {
        let i = 0;
      }
    }
    this.internalValue = this.documents.map((doc) => {
      return doc.fileToupload
          ? doc.fileToupload
          : new File([], doc.name || "unknown.file", {});
    });
    this.setValueView(this.documents.map((value) => value.documentId));
    this.setValueEntry(
        this.documents.map((value) => value.documentId),
        setDataImmediate
    );
    this.isProcessing = false;
  }

  public async fillIndexerValue(indexerField: string, indexerSelector): Promise<any> {
    if ("name" === indexerSelector) {
      return this.documents
          .filter((value) => !!value)
          .map((value) => value.name)
          .join(", ");
    }

    if ("category" === indexerSelector) {
      return this.documents
          .filter((value) => !!value)
          .map((value) => value.category)
          .join(", ");
    }

    return super.fillIndexerValue(indexerField, indexerSelector);
  }

  private async adoptDocuments(sumbitData: any) {
    let doc: DocumentFile;

    let typ = sumbitData["type"];
    let refId = sumbitData["referenceId"];
    if (this.entry?.metadata?.alternateReferenceType){
      typ = this.entry.root?.resolvePlaceholders(this.entry?.metadata?.alternateReferenceType);
    }
    if (this.entry?.metadata?.alternateReferenceId) {
      refId = this.entry.root?.resolvePlaceholders(this.entry?.metadata?.alternateReferenceId);
    }
    if (typ != "documentbox") {
      for (doc of Array.from(this.documents)) {
        if(doc.fileToupload){
          const uploadResult = await documentServiceV2.adoptDocument(doc,typ,refId);
        }
      }
    }
  }

  private async addParties(involvedParties:{
    portalId : string //reference to identify the reason the user was added as party
    roleId? : string //role of user/org to grant to
    contactId? : string //id of user/org to grant to
    partyRoleId?: string,
    contactType : ("USER_IN_CURRENT_ORGANISATION" |    "USER" |    "ORGANISATION" |    "ALL_USERS" |    "BCCA" |    "USERS_WITH_ROLE" |    "ORGANISATIONS_WITH_ROLE")
  }[] | undefined, documents: DocumentFile[]) {
    if(!involvedParties || !Array.isArray(involvedParties) || involvedParties.length <= 0) return;
    let doc: DocumentFile;
    for (doc of Array.from(documents)) {
      if(!doc.documentId) continue;

      for (let involvedParty of involvedParties) {
        if(!involvedParty.contactType) continue;
        await partyService.addParty('document',doc.documentId, involvedParty.contactType, involvedParty.contactId || involvedParty.roleId || null, involvedParty.partyRoleId || null , involvedParty.portalId)
      }
    }
  }

  private async uploadDocument(doc: DocumentFile) {
    this.currentUploadingDocument?.push(doc.name || '');
    let categoryId = this.entry.metadata.categoryId ? this.entry.metadata.categoryId : this.entry.root?.resolveFieldValue(this.entry.metadata.categoryIdTemplate);
    let typeIds = this.entry.metadata.typeIds ? this.entry.metadata.typeIds : this.entry.root?.resolveFieldValue(this.entry.metadata.typeIdsTemplate);
    let description = this.entry.metadata.description ? this.entry.metadata.description : this.entry.root?.resolveFieldValue(this.entry.metadata.descriptionTemplate);
    doc.category = categoryId;
    doc.type = typeIds;
    doc.description = description;
    doc.activityId = this.$store.state.activity.currentActivity.id;

    let recordId = '';
    if((this.metadata?.attachDynamicProperties || this.metadata?.additionalDynamicProperties) && this.entry.root){
      recordId = await documentServiceV2.createDocumentEntry(doc, this.entry.root.formData, this.entry.root, this.metadata?.attachDynamicProperties, this.metadata?.additionalDynamicProperties, this.metadata?.additionalDynamicPropertiesIndexers);
    }else{
      recordId = await documentServiceV2.createDocumentEntry(doc, null, null, false, null, null);
    }

    //create entry
    doc.documentId = recordId;

    //upload doc
    const uploadResult = await documentServiceV2.uploadDocument(doc);
    if (!uploadResult) {
      //revert if failed
      console.warn("failed to upload file", doc, uploadResult);
      doc.documentId = "nan";
      this.amountOfUploadedDocuments += 1;
      doc.uploaded = true;
      return false;
    } else {
      this.amountOfUploadedDocuments += 1;
      doc.uploaded = true;
      return true;
    }


  }
}
